import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FileCode2, GitBranch, Clock, Hash, BarChart3, Loader2, Sparkles,
  CheckCircle2, ArrowRight, Files, TrendingDown, TrendingUp, Calendar,
  Linkedin,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminServerModules
 * ------------------
 * Read-only inspector page that shows the server.py modularization progress
 * and lists every router file in /app/backend/routes/ with its line count,
 * endpoint count, and last-modified date.
 *
 * Iter53: also pulls /api/admin/server-modules/delta for a "diff vs last week"
 * momentum overlay at the top.
 *
 * Admin-only — pulls from /api/admin/server-modules which is gated by
 * require_role(['admin']).
 */
const AdminServerModules = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [delta, setDelta] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [modR, deltaR, shareR] = await Promise.all([
          axios.get(`${API}/admin/server-modules`, { headers }),
          axios.get(`${API}/admin/server-modules/delta`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/admin/build/share-stats?days=7`, { headers }).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setData(modR.data);
        setDelta(deltaR.data);
        setShareStats(shareR.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <Card className="bg-rose-500/10 border-rose-500/30 max-w-lg">
          <CardContent className="p-6 text-rose-200">
            <h2 className="text-lg font-bold mb-2">Could not load module inspector</h2>
            <p className="text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, modules } = data;
  const { server_py, routes_total } = summary;
  const reductionPct = server_py.reduction_pct || 0;

  // Sort modules: extracted-first, then by line count desc
  const sorted = [...modules].sort((a, b) => {
    if (a.is_modularized_extraction !== b.is_modularized_extraction) {
      return a.is_modularized_extraction ? -1 : 1;
    }
    return b.line_count - a.line_count;
  });

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-server-modules-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <Badge className="bg-emerald-500/15 text-cyan-300 border-emerald-500/30 text-[11px] font-bold tracking-widest mb-2">
            ADMIN · MODULE INSPECTOR
          </Badge>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Files className="w-7 h-7 text-emerald-400" /> Server Modules
          </h1>
          <p className="text-slate-400 mt-1 text-sm">
            Live view of server.py modularization progress + every router file's stats.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="border-white/10"
          data-testid="refresh-modules-btn"
        >
          Refresh
        </Button>
      </div>

      {/* Iter53 — Momentum overlay (7-day delta) */}
      <DeltaOverlay delta={delta} />

      {/* Iter68b — Share-link analytics tile (last 7 days) */}
      <ShareStatsTile stats={shareStats} />

      {/* Server.py summary card */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/20" data-testid="server-py-summary-card">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <FileCode2 className="w-5 h-5 text-emerald-400" />
                <span className="font-bold text-white">server.py</span>
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[11px] font-bold tracking-widest">
                  MONOLITH (BREAKING UP)
                </Badge>
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-bold text-white" data-testid="server-py-line-count">
                  {server_py.line_count.toLocaleString()}
                </span>
                <span className="text-slate-400">lines</span>
                <span className="text-slate-400 text-sm">/ started at {server_py.starting_line_count.toLocaleString()}</span>
              </div>
              <div className="text-sm text-slate-400 mt-1">
                <span className="text-emerald-400 font-semibold">{server_py.lines_reduced.toLocaleString()} lines</span>
                {' '}removed ({reductionPct}% reduction) · {server_py.endpoint_count} endpoints remain
              </div>
            </div>
            <div className="w-full lg:w-1/3">
              <div className="text-xs text-slate-400 mb-1.5 flex justify-between">
                <span>Modularization progress</span>
                <span className="text-emerald-400 font-bold">{reductionPct}%</span>
              </div>
              <Progress value={reductionPct} className="h-3 bg-white/5" />
              <div className="text-xs text-slate-400 mt-1.5">
                Last touched: {fmtDate(server_py.last_modified)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aggregate routes-total card */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-slate-900/60 border-white/10" data-testid="stat-modules-total">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Router files</div>
            <div className="text-3xl font-bold text-white">{routes_total.module_count}</div>
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              {routes_total.extracted_modules} extracted from server.py
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-white/10" data-testid="stat-endpoints-total">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Endpoints in routes/</div>
            <div className="text-3xl font-bold text-white">{routes_total.endpoint_count}</div>
            <div className="text-xs text-slate-400 mt-1">vs {server_py.endpoint_count} still in server.py</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-white/10" data-testid="stat-loc-total">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Lines in routes/</div>
            <div className="text-3xl font-bold text-white">{routes_total.line_count.toLocaleString()}</div>
            <div className="text-xs text-slate-400 mt-1">FastAPI: {summary.fastapi_live_route_count} live routes</div>
          </CardContent>
        </Card>
      </div>

      {/* Module table */}
      <Card className="bg-slate-900/60 border-white/10" data-testid="modules-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                  <th className="text-left py-3 px-4">Module</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Lines</th>
                  <th className="text-right py-3 px-4">Endpoints</th>
                  <th className="text-left py-3 px-4">Last modified</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => (
                  <tr
                    key={m.name}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`module-row-${m.name}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <FileCode2 className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-mono text-slate-300">{m.name}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 ml-6">{m.path}</div>
                    </td>
                    <td className="py-3 px-4">
                      {m.is_modularized_extraction ? (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest">
                          {(m.extracted_in || '').toUpperCase()} · EXTRACTED
                        </Badge>
                      ) : (
                        <Badge className="bg-white/5 text-slate-400 border-white/10 text-[11px] font-bold tracking-widest">
                          PRE-EXISTING
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-bold text-white tabular-nums">{m.line_count.toLocaleString()}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-bold text-emerald-400 tabular-nums">{m.endpoint_count}</span>
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtDate(m.last_modified)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Future-extraction backlog (informative) */}
      <Card className="bg-slate-900/40 border-white/5" data-testid="backlog-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <GitBranch className="w-4 h-4 text-cyan-400" />
            <span className="font-bold text-white">Next extraction candidates</span>
          </div>
          <p className="text-sm text-slate-400 mb-3">
            Identified inside server.py — well-isolated, low-risk batches:
          </p>
          <ul className="space-y-1.5 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              ~10 cloud connector endpoints → <code className="text-cyan-300">routes/cloud_connectors.py</code>
            </li>
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              7 customer-economics endpoints → <code className="text-cyan-300">routes/customer_economics.py</code>
            </li>
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              6 MFA endpoints → <code className="text-cyan-300">routes/mfa.py</code> (mid-risk — touches OTP/blacklist state)
            </li>
            <li className="flex items-center gap-2 text-amber-300">
              <ArrowRight className="w-3.5 h-3.5 shrink-0" />
              14 login/register/JWT/OTP endpoints — high-risk, save for last
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminServerModules;

// ─── Delta overlay component ────────────────────────────────────────────────
const DeltaOverlay = ({ delta }) => {
  if (!delta) return null;

  // First-run state — no historical snapshot yet.
  if (delta.first_run || !delta.delta) {
    return (
      <Card className="bg-emerald-500/5 border-emerald-500/20" data-testid="delta-overlay-first-run">
        <CardContent className="p-4 flex items-center gap-3 text-sm">
          <Calendar className="w-4 h-4 text-cyan-400 shrink-0" />
          <span className="text-slate-300">
            Capturing baseline snapshot. Momentum delta will appear after the next run (within 24h).
          </span>
        </CardContent>
      </Card>
    );
  }

  const d = delta.delta;
  const days = delta.days_compared || 0;
  // For these metrics, NEGATIVE delta is GOOD (lines reduced, endpoints moved out of monolith).
  const goodIfNegative = ['monolith_lines', 'endpoints_remaining'];

  const fmt = (n) => (n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString());

  const stats = [
    {
      key: 'monolith_lines',
      label: 'Lines removed from server.py',
      raw: d.monolith_lines,
      display: fmt(d.monolith_lines),
    },
    {
      key: 'extracted_from_monolith',
      label: 'New extracted modules',
      raw: d.extracted_from_monolith,
      display: fmt(d.extracted_from_monolith),
    },
    {
      key: 'endpoints_remaining',
      label: 'Endpoints moved out of monolith',
      raw: d.endpoints_remaining,
      display: fmt(d.endpoints_remaining),
    },
    {
      key: 'router_files',
      label: 'New router files',
      raw: d.router_files,
      display: fmt(d.router_files),
    },
  ];

  return (
    <Card
      className="bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border-emerald-500/30"
      data-testid="delta-overlay"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-cyan-400" />
            <span className="font-bold text-white">Last {days}-day momentum</span>
            <Badge className="bg-emerald-500/15 text-cyan-300 border-emerald-500/30 text-[11px] font-bold tracking-widest">
              SINCE {delta.previous?.date || '—'}
            </Badge>
          </div>
          <span className="text-xs text-slate-400">vs {delta.current?.date || '—'}</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => {
            const positiveChange = goodIfNegative.includes(s.key)
              ? s.raw < 0
              : s.raw > 0;
            const negativeChange = goodIfNegative.includes(s.key)
              ? s.raw > 0
              : s.raw < 0;
            const tone = positiveChange ? 'text-emerald-400' : negativeChange ? 'text-rose-400' : 'text-slate-400';
            const Icon = positiveChange ? TrendingDown : negativeChange ? TrendingUp : null;
            return (
              <div key={s.key} className="space-y-0.5" data-testid={`delta-${s.key}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`text-2xl font-bold tabular-nums ${tone}`}>{s.display}</span>
                  {Icon && <Icon className={`w-4 h-4 ${tone}`} />}
                </div>
                <div className="text-xs text-slate-400">{s.label}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * ShareStatsTile — Iter68b
 * Compact admin tile that shows last 7-day LinkedIn / share-link
 * click & visit counts. Renders a friendly "first run" empty state if no
 * events have been recorded yet.
 */
const ShareStatsTile = ({ stats }) => {
  if (!stats) return null;

  if (stats.first_run) {
    return (
      <Card
        className="bg-slate-900/40 border-white/5"
        data-testid="share-stats-tile-first-run"
      >
        <CardContent className="p-4 flex items-center gap-3 text-sm">
          <Linkedin className="w-4 h-4 text-sky-400" />
          <span className="text-slate-400">
            <span className="text-slate-200 font-semibold">No share-link
            traffic yet.</span> Click the LinkedIn share button on
            <span className="text-cyan-300"> /build/momentum</span> to start
            collecting attribution.
          </span>
        </CardContent>
      </Card>
    );
  }

  const totalClicks = stats.totals?.clicks || 0;
  const totalVisits = stats.totals?.visits || 0;
  const headline =
    stats.sources?.[0]?.source || 'multiple sources';

  return (
    <Card
      className="bg-gradient-to-br from-sky-500/10 via-slate-900/60 to-slate-900/40 border-sky-500/20"
      data-testid="share-stats-tile"
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Linkedin className="w-4 h-4 text-sky-400" />
          <span className="text-[11px] uppercase tracking-widest text-sky-300 font-bold">
            LAST {stats.days}-DAY SHARE TRAFFIC
          </span>
          <Badge
            className="ml-auto bg-emerald-500/15 text-sky-300 border-sky-500/30 text-[11px]"
          >
            {headline}
          </Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div data-testid="share-stats-clicks">
            <div className="text-2xl font-bold text-white tabular-nums">
              {totalClicks.toLocaleString()}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">Share clicks</div>
          </div>
          <div data-testid="share-stats-visits">
            <div className="text-2xl font-bold text-white tabular-nums">
              {totalVisits.toLocaleString()}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">UTM-tagged visits</div>
          </div>
          <div data-testid="share-stats-reach">
            <div className="text-2xl font-bold text-emerald-300 tabular-nums">
              {totalClicks > 0
                ? `${Math.round((totalVisits / totalClicks) * 100)}%`
                : '—'}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">Reach (visits / clicks)</div>
          </div>
        </div>
        {stats.sources && stats.sources.length > 1 && (
          <div className="mt-4 pt-3 border-t border-white/5 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {stats.sources.slice(0, 6).map((s) => (
              <div
                key={s.source}
                className="flex items-center justify-between bg-slate-900/40 px-2.5 py-1.5 rounded"
                data-testid={`share-stats-source-${s.source}`}
              >
                <span className="text-slate-300 font-medium truncate">
                  {s.source}
                </span>
                <span className="text-slate-400 tabular-nums">
                  {s.clicks}c · {s.visits}v
                </span>
              </div>
            ))}
          </div>
        )}
        <RoleBreakdown roles={stats.roles_breakdown} days={stats.days} />
      </CardContent>
    </Card>
  );
};

// ─── Role Breakdown ─────────────────────────────────────────────────────────
// Iter73c — surfaces the per-role click attribution from the share-stats
// API. Hidden when no role-tagged clicks exist (graceful first-run state).
const ROLE_META = {
  engineer: { label: 'Engineers', icon: '⚙️', color: 'bg-cyan-400' },
  cto:      { label: 'CTOs',      icon: '🛡️', color: 'bg-emerald-400' },
  vc:       { label: 'VCs',       icon: '💼', color: 'bg-cyan-400' },
  founder:  { label: 'Founders',  icon: '🚀', color: 'bg-amber-400' },
  other:    { label: 'Other',     icon: '·',  color: 'bg-slate-500' },
};

const RoleBreakdown = ({ roles, days }) => {
  if (!roles || roles.length === 0) return null;
  const total = roles.reduce((s, r) => s + (r.clicks || 0), 0);
  if (total === 0) return null;
  const top = roles[0];
  const meta = ROLE_META[top.role] || ROLE_META.other;
  return (
    <div className="mt-4 pt-3 border-t border-white/5" data-testid="share-stats-roles">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-bold">
          ROLES SHARING THIS WEEK
        </span>
        <span
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[11px] font-bold"
          data-testid="share-stats-top-role"
        >
          {meta.icon} {meta.label} ({top.clicks})
        </span>
      </div>
      <div className="space-y-1.5">
        {roles.slice(0, 5).map((r) => {
          const m = ROLE_META[r.role] || ROLE_META.other;
          const pct = Math.max(2, Math.round((r.clicks / total) * 100));
          return (
            <div
              key={r.role}
              className="flex items-center gap-2 text-xs"
              data-testid={`share-stats-role-${r.role}`}
            >
              <span className="w-20 text-slate-300 font-medium">
                {m.icon} {m.label}
              </span>
              <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full ${m.color} transition-all duration-700`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-12 text-right text-slate-400 tabular-nums">
                {r.clicks} · {pct}%
              </span>
            </div>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-400 mt-2">
        Aggregated from role-tagged LinkedIn share clicks · last {days} days
      </div>
    </div>
  );
};
